// ─── CLIENT-SIDE ENCRYPTION WITH SERVER-HELD KEYS ───────────────────
// Keys are fetched from backend (/api/encryption/key)
// Server holds master key in Vercel Secrets
// Never derives keys from public data

const ALGO = { name: "AES-GCM", length: 256 };

// Store the current company's encryption key in memory
// (Not localStorage - it's sensitive and auto-cleared on page reload)
let currentCompanyKey = null;

/**
 * Set the company encryption key (call this after login with key from /api/encryption/key)
 * @param {string} keyHex - 256-bit key in hex format from backend
 */
export function setCompanyEncryptionKey(keyHex) {
  if (!keyHex) {
    console.warn('[Crypto] Setting null encryption key');
    currentCompanyKey = null;
    return;
  }
  currentCompanyKey = keyHex;
  console.log('[Crypto] Company encryption key loaded');
}

/**
 * Get the current company encryption key
 * @returns {string|null}
 */
export function getCompanyEncryptionKey() {
  return currentCompanyKey;
}

/**
 * Clear the encryption key (call on logout)
 */
export function clearCompanyEncryptionKey() {
  currentCompanyKey = null;
  console.log('[Crypto] Company encryption key cleared');
}

/**
 * Derive AES-256 key from server-provided hex key with salt
 * @param {string} keyHex - 256-bit key in hex format
 * @param {Uint8Array} [providedSalt] - Optional existing salt (for decryption). If not provided, generates new one (for encryption)
 * @returns {Promise<{key, salt}>} - WebCrypto key and salt buffer
 */
async function deriveKeyFromServerKey(keyHex, providedSalt = null) {
  if (!keyHex) throw new Error('Server-provided key required');

  const enc = new TextEncoder();
  const keyMat = await crypto.subtle.importKey(
    "raw",
    enc.encode(keyHex),
    "PBKDF2",
    false,
    ["deriveKey"]
  );

  // Use provided salt (for decryption) or generate new one (for encryption)
  const salt = providedSalt || crypto.getRandomValues(new Uint8Array(16));

  const key = await crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: salt,
      iterations: 100_000,
      hash: "SHA-256"
    },
    keyMat,
    ALGO,
    false,
    ["encrypt", "decrypt"]
  );

  return { key, salt };
}

/**
 * Encrypt a string value with server-held key
 * Format: "serverenc:salt:iv:authTag:ciphertext"
 * @param {string} plaintext - Data to encrypt
 * @param {string} keyHex - Encryption key (from backend)
 * @returns {Promise<string>} - Encrypted value with salt, IV, auth tag
 */
export async function encryptField(plaintext, keyHex) {
  if (!plaintext || !keyHex) return plaintext;
  
  try {
    const { key, salt } = await deriveKeyFromServerKey(keyHex);
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const enc = new TextEncoder();
    
    // Encrypt with AES-GCM
    const data = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv },
      key,
      enc.encode(String(plaintext))
    );
    
    // Combine into single string: "serverenc:salt:iv:authTag:ciphertext"
    const saltB64 = btoa(String.fromCharCode(...salt));
    const ivB64 = btoa(String.fromCharCode(...iv));
    const dataB64 = btoa(String.fromCharCode(...new Uint8Array(data)));
    
    return `serverenc:${saltB64}:${ivB64}:${dataB64}`;
  } catch (e) {
    console.error('[Crypto] Encrypt failed:', e);
    return plaintext;
  }
}

/**
 * Decrypt a previously encrypted value
 * @param {string} ciphertext - Encrypted data in format "serverenc:salt:iv:ciphertext"
 * @param {string} keyHex - Encryption key (from backend)
 * @returns {Promise<string>} - Original plaintext
 */
export async function decryptField(ciphertext, keyHex) {
  if (!ciphertext || !keyHex || !String(ciphertext).startsWith("serverenc:")) {
    return ciphertext;
  }

  try {
    const [, saltB64, ivB64, dataB64] = String(ciphertext).split(":");
    const salt = Uint8Array.from(atob(saltB64), c => c.charCodeAt(0));
    const iv = Uint8Array.from(atob(ivB64), c => c.charCodeAt(0));
    const data = Uint8Array.from(atob(dataB64), c => c.charCodeAt(0));

    // Pass the extracted salt to deriveKeyFromServerKey so it derives the same key used for encryption
    const { key } = await deriveKeyFromServerKey(keyHex, salt);
    const dec = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv },
      key,
      data
    );

    return new TextDecoder().decode(dec);
  } catch (e) {
    console.error('[Crypto] Decrypt failed:', e);
    return '[encrypted]';
  }
}

/**
 * Encrypt an object's sensitive fields using current company key
 * @param {object} obj - Object with fields to encrypt
 * @param {string[]} fields - Field names to encrypt
 * @returns {Promise<object>} - Object with encrypted fields
 */
export async function encryptFields(obj, fields) {
  if (!obj || !fields?.length || !currentCompanyKey) {
    return obj;
  }
  
  const result = { ...obj };
  await Promise.all(
    fields.map(async field => {
      if (result[field] != null) {
        result[field] = await encryptField(String(result[field]), currentCompanyKey);
      }
    })
  );
  return result;
}

/**
 * Decrypt an object's sensitive fields using current company key
 * @param {object} obj - Object with encrypted fields
 * @param {string[]} fields - Field names to decrypt
 * @returns {Promise<object>} - Object with decrypted fields
 */
export async function decryptFields(obj, fields) {
  if (!obj || !fields?.length || !currentCompanyKey) {
    return obj;
  }
  
  const result = { ...obj };
  await Promise.all(
    fields.map(async field => {
      if (result[field] != null) {
        result[field] = await decryptField(String(result[field]), currentCompanyKey);
      }
    })
  );
  return result;
}

/**
 * Decrypt an array of objects using current company key
 * @param {object[]} rows - Array of objects with encrypted fields
 * @param {string[]} fields - Field names to decrypt
 * @returns {Promise<object[]>} - Array with decrypted fields
 */
export async function decryptRows(rows, fields) {
  if (!currentCompanyKey || !rows?.length || !fields?.length) {
    return rows;
  }
  
  return Promise.all(rows.map(row => decryptFields(row, fields)));
}

// ─── BACKWARDS COMPATIBILITY (for gradual migration) ───────────────
// Old deriveKey function - still works but deprecated
// Remove this after full frontend migration
const OLD_ALGO = { name: "AES-GCM", length: 256 };

async function deriveKey(companyId) {
  console.warn('[Crypto] Using deprecated deriveKey - migrate to server-held keys');
  const enc = new TextEncoder();
  const keyMat = await crypto.subtle.importKey("raw", enc.encode(companyId), "PBKDF2", false, ["deriveKey"]);
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", salt: enc.encode("crossregguard-salt"), iterations: 100_000, hash: "SHA-256" },
    keyMat,
    OLD_ALGO,
    false,
    ["encrypt", "decrypt"]
  );
}

// Old encrypt function - still works but deprecated
export async function encrypt(plaintext, companyId) {
  console.warn('[Crypto] Using deprecated encrypt - migrate to server-held keys');
  if (!plaintext || !companyId) return plaintext;
  try {
    const key = await deriveKey(companyId);
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const enc = new TextEncoder();
    const data = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, enc.encode(String(plaintext)));
    const ivB64 = btoa(String.fromCharCode(...iv));
    const dataB64 = btoa(String.fromCharCode(...new Uint8Array(data)));
    return `enc:${ivB64}:${dataB64}`;
  } catch (e) {
    console.error("[crypto] encrypt failed", e);
    return plaintext;
  }
}

// Old decrypt function - still works but deprecated
export async function decrypt(ciphertext, companyId) {
  console.warn('[Crypto] Using deprecated decrypt - migrate to server-held keys');
  if (!ciphertext || !companyId || !String(ciphertext).startsWith("enc:")) return ciphertext;
  try {
    const [, ivB64, dataB64] = String(ciphertext).split(":");
    const iv = Uint8Array.from(atob(ivB64), c => c.charCodeAt(0));
    const data = Uint8Array.from(atob(dataB64), c => c.charCodeAt(0));
    const key = await deriveKey(companyId);
    const dec = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, data);
    return new TextDecoder().decode(dec);
  } catch (e) {
    console.error("[crypto] decrypt failed", e);
    return "[encrypted]";
  }
}
